import React, { useEffect, useState } from 'react'
import * as PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Icon from '../../../Icons'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../../FormLegacy/FormFieldsV2/TextAreaField'
import API from '../../../../api/api'

const CustomReplyWindow = ({ inboxId, editCustomReplyData, handleCancel, updateRepliesList }) => {
  const { t } = useTranslation(['chat'])
  const [isSubmitting, setSubmitting] = useState(false)
  const [customReply, setCustomReply] = useState({ title: '', message: '' })

  const isEditMode = editCustomReplyData && !!Object.keys(editCustomReplyData).length
  const enableSubmit = !!customReply.title && !!customReply.message

  const handleChange = (e) => {
    const { value, name } = e.target
    setCustomReply((prevState) => ({ ...prevState, [name]: value }))
  }

  const handleCreate = async () => {
    setSubmitting(true)
    try {
      await API.createInboxCustomReply(inboxId, customReply).then((response) => {
        updateRepliesList(response.data)
      })
      setSubmitting(false)
      handleCancel()
    } catch ({ message }) {
      throw new Error(message)
    }
  }

  const handleUpdate = async () => {
    setSubmitting(true)
    try {
      await API.updateInboxCustomReply(inboxId, customReply).then((response) => {
        updateRepliesList(response.data)
      })
      setSubmitting(false)
      handleCancel()
    } catch ({ message }) {
      throw new Error(message)
    }
  }

  const handleRemove = async () => {
    setSubmitting(true)
    try {
      await API.removeInboxCustomReply(inboxId, customReply).then(() => {
        updateRepliesList({ ...customReply, toRemove: true })
      })
      setSubmitting(false)
      handleCancel()
    } catch ({ message }) {
      throw new Error(message)
    }
  }

  useEffect(() => {
    if (isEditMode) {
      setCustomReply(editCustomReplyData)
    }
  }, [editCustomReplyData])

  return (
    <div className="custom-reply-window">
      <div className={`custom-reply-window__container`}>
        <div className="window-header">
          <div className="left-side">
            <div className="title">
              {isEditMode
                ? t('chat:customReplyWindow.editTitle')
                : t('chat:customReplyWindow.createTitle')}
            </div>
          </div>
          <div className="right-side">
            <div className="navi-button" onClick={handleCancel}>
              <Icon icon="cross" />
            </div>
          </div>
        </div>
        <div className="main-view">
          <TextField
            maxLength="100"
            name="title"
            label={t('chat:customReplyWindow.replyTitle')}
            placeholder={t('chat:customReplyWindow.enterReplyTitle')}
            onChange={handleChange}
            value={customReply.title}
            disabled={isSubmitting}
          />
          <TextAreaField
            rows={5}
            name="message"
            label={t('chat:customReplyWindow.message')}
            maxLength="160"
            placeholder={t('chat:customReplyWindow.typeMessage')}
            value={customReply.message}
            onChange={handleChange}
            disabled={isSubmitting}
          />
        </div>
        <div className="window-footer">
          <div className="footer-buttons">
            <button
              type="submit"
              className={`button button-gray is-block ${isSubmitting ? 'is-loading' : ''}`}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t('common:actions.cancel')}
            </button>
            {isEditMode ? (
              <React.Fragment>
                <button
                  type="submit"
                  className={`button button-black is-block ${isSubmitting ? 'is-loading' : ''}`}
                  onClick={handleRemove}
                  disabled={isSubmitting}
                >
                  {t('chat:customReplyWindow.remove')}
                </button>
                <button
                  type="submit"
                  className={`button button-primary is-block ${isSubmitting ? 'is-loading' : ''}`}
                  onClick={handleUpdate}
                  disabled={isSubmitting || !customReply.message || !customReply.title}
                >
                  {t('chat:customReplyWindow.saveReply')}
                </button>
              </React.Fragment>
            ) : (
              <button
                type="submit"
                className={`button button-primary is-block ${isSubmitting ? 'is-loading' : ''}`}
                onClick={handleCreate}
                disabled={isSubmitting || !enableSubmit}
              >
                {t('chat:customReplyWindow.createReply')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

CustomReplyWindow.propTypes = {
  inboxId: PropTypes.number,
  editCustomReplyData: PropTypes.object,
  handleCancel: PropTypes.func,
  updateRepliesList: PropTypes.func,
}

export default CustomReplyWindow
